import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  HiBadgeCheck,
  HiIdentification,
  HiLocationMarker,
  HiMail,
  HiOfficeBuilding,
  HiPhone,
  HiUserCircle,
} from "react-icons/hi";
import {
  getTranslatedText,
  getTranslatedTextSelector,
} from "../../../redux/slices/translatedText/getTranslatedText";

const TeacherIdProfileCard = ({
  teacherData,
  handleConfirmTeacherDetail,
  handleCancelTeacherDetail,
  confirmTeacherLoading = false,
  modalView = false,
}) => {
  const teacher = teacherData?.[0];

  const dispatch = useDispatch();

  const getTextTranslated = useSelector(getTranslatedTextSelector);

  useEffect(() => {
    getText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getText = async () => {
    await dispatch(getTranslatedText(localStorage.getItem("l_id"))).unwrap();
  };

  const getLabel = (pageKey, fallback) =>
    getTextTranslated?.data?.find((item) => item?.pageKey === pageKey)
      ?.keyValue || fallback;

  const displayValue = (value) => value || "-";
  const teacherProfileData = [
    {
      label: getLabel("teacher_id", "Teacher ID"),
      value: teacher?.TeacherID,
    },
    {
      label: getLabel("teacher_name", "Teacher Name"),
      value: teacher?.TeacherName,
    },
    {
      label: getLabel("dob", "D.O.B"),
      value: teacher?.DOB,
    },
    {
      label: getLabel("gender", "Gender"),
      value: teacher?.GENDER,
    },
    {
      label: getLabel("mobile_number", "Mobile Number"),
      value: teacher?.MobileNumber,
    },
    {
      label: getLabel("whatsapp_number", "Whatsapp Number"),
      value: teacher?.WhatsappNumber || "-",
    },
    {
      label: getLabel("email_id", "Email ID"),
      value: teacher?.EmailId,
    },
    {
      label: getLabel("school_udise", "School UDISE"),
      value: teacher?.School_UDISE,
    },
    {
      label: getLabel("school_name", "School Name"),
      value: teacher?.SCHNAME,
    },
    {
      label: getLabel("state", "State"),
      value: teacher?.State,
    },
    {
      label: getLabel("district", "District"),
      value: teacher?.District,
    },
    {
      label: getLabel("block_zone", "Block/Zone"),
      value: teacher?.Block_Zone,
    },
    {
      label: getLabel("cluster_name", "Cluster Name"),
      value: teacher?.ClusterName,
    },
  ];

  const teacherName = displayValue(teacher?.TeacherName);
  const teacherInitials =
    teacherName === "-"
      ? "T"
      : teacherName
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((name) => name[0])
          .join("")
          .toUpperCase();

  const teacherProfileSections = [
    {
      title: getLabel("personal_information", "Personal Information"),
      icon: <HiIdentification />,
      items: [
        {
          label: getLabel("teacher_id", "Teacher ID"),
          value: teacher?.TeacherID,
        },
        {
          label: getLabel("teacher_name", "Teacher Name"),
          value: teacher?.TeacherName,
        },
        {
          label: getLabel("dob", "D.O.B"),
          value: teacher?.DOB,
        },
        {
          label: getLabel("gender", "Gender"),
          value: teacher?.GENDER,
        },
      ],
    },
    {
      title: getLabel("contact_details", "Contact Details"),
      icon: <HiPhone />,
      items: [
        {
          label: getLabel("mobile_number", "Mobile Number"),
          value: teacher?.MobileNumber,
        },
        {
          label: getLabel("whatsapp_number", "Whatsapp Number"),
          value: teacher?.WhatsappNumber,
        },
        {
          label: getLabel("email_id", "Email ID"),
          value: teacher?.EmailId,
        },
      ],
    },
    {
      title: getLabel("school_details", "School Details"),
      icon: <HiOfficeBuilding />,
      items: [
        {
          label: getLabel("school_name", "School Name"),
          value: teacher?.SCHNAME,
          wide: true,
        },
        {
          label: getLabel("school_udise", "School UDISE"),
          value: teacher?.School_UDISE,
        },
        {
          label: getLabel("state", "State"),
          value: teacher?.State,
        },
        {
          label: getLabel("district", "District"),
          value: teacher?.District,
        },
        {
          label: getLabel("block_zone", "Block/Zone"),
          value: teacher?.Block_Zone,
        },
        {
          label: getLabel("cluster_name", "Cluster Name"),
          value: teacher?.ClusterName,
        },
      ],
    },
  ];

  if (!modalView) {
    return (
      <div className="flex flex-col h-[100vh] max-h-[100vh]">
        <div className="flex-1 overflow-y-auto bg-[#e9ebfa] p-4">
          <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12">
              {teacherProfileData?.map((item) => (
                <div key={item.label}>
                  <p className="text-sm text-gray-400 mb-1">{item.label}</p>
                  <p className="text-base md:text-lg text-gray-900 font-medium break-words">
                    {displayValue(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t p-4">
          <h2 className="text-center text-sm md:text-base font-medium mb-4">
            {getLabel(
              "confirm_once_again",
              "Please confirm the details once again",
            )}
          </h2>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              className="w-full sm:w-auto bg-[#E7EAFF] text-[#3C57AD] px-10 py-3 rounded-[10px] disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleCancelTeacherDetail}
              disabled={confirmTeacherLoading}
            >
              {getLabel("not_txt", "No")}
            </button>

            <button
              className={`w-full sm:w-auto text-white px-10 py-3 rounded-[10px] flex items-center justify-center gap-2 ${
                confirmTeacherLoading
                  ? "bg-[#3a3e58] cursor-wait"
                  : "bg-gradient-to-r from-[#2C3EA2] to-[#192867]"
              }`}
              onClick={handleConfirmTeacherDetail}
              disabled={confirmTeacherLoading}
            >
              {confirmTeacherLoading ? (
                <>
                  <span className="spinner-border animate-spin inline-block w-4 h-4 border-4 rounded-full"></span>
                  <span>{getLabel("label_loading", "Loading")}...</span>
                </>
              ) : (
                getLabel("yes_txt", "Yes")
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-[#F5F7FC] max-h-[88vh] rounded-[18px] overflow-hidden text-left">
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white border-b border-[#E4E8F3] px-5 py-5 md:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] bg-[#E9EDFF] text-xl font-bold text-[#263795]">
                {teacherInitials}
              </div>
              <div className="min-w-0 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl md:text-2xl font-bold leading-7 text-[#101942] break-words">
                    {teacherName}
                  </h2>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF7EF] px-3 py-1 text-xs font-semibold text-[#1F7A45]">
                    <HiBadgeCheck className="text-base" />
                    {getLabel("ready_to_confirm", "Ready to confirm")}
                  </span>
                </div>
                <p className="mt-2 text-left text-sm text-[#667085]">
                  {getLabel(
                    "confirm_once_again",
                    "Please confirm the details once again",
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:min-w-[360px]">
              <div className="rounded-[12px] border border-[#E4E8F3] bg-[#F8FAFF] px-4 py-3">
                <p className="text-xs font-medium text-[#667085]">
                  {getLabel("teacher_id", "Teacher ID")}
                </p>
                <p className="mt-1 text-base font-bold text-[#101942]">
                  {displayValue(teacher?.TeacherID)}
                </p>
              </div>
              <div className="rounded-[12px] border border-[#E4E8F3] bg-[#F8FAFF] px-4 py-3">
                <p className="text-xs font-medium text-[#667085]">
                  {getLabel("school_udise", "School UDISE")}
                </p>
                <p className="mt-1 text-base font-bold text-[#101942]">
                  {displayValue(teacher?.School_UDISE)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 md:px-8 md:py-6">
          <div className="mx-auto w-full max-w-6xl space-y-4">
            {teacherProfileSections.map((section) => (
              <section
                key={section.title}
                className="rounded-[16px] border border-[#E4E8F3] bg-white p-4 shadow-sm md:p-5"
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#EEF2FF] text-xl text-[#2C3EA2]">
                    {section.icon}
                  </span>
                  <h3 className="text-base font-bold text-[#101942]">
                    {section.title}
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {section.items.map((item) => (
                    <div
                      key={`${section.title}-${item.label}`}
                      className={`rounded-[12px] border border-[#EEF1F8] bg-[#FAFBFF] px-4 py-3 text-left transition hover:border-[#C8D1FF] hover:bg-white ${
                        item.wide ? "md:col-span-2 xl:col-span-2" : ""
                      }`}
                    >
                      <p className="text-xs font-semibold uppercase text-[#7B849B]">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm md:text-base font-semibold leading-6 text-[#101942] break-words">
                        {displayValue(item.value)}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <div className="rounded-[14px] border border-[#D7DEFA] bg-[#F0F4FF] px-4 py-3 text-[#263795]">
              <div className="flex items-start gap-3">
                <HiUserCircle className="mt-0.5 shrink-0 text-xl" />
                <p className="text-sm font-medium leading-6">
                  {getLabel(
                    "review_teacher_details_note",
                    "Review all details carefully before confirming. Choose No if any information needs correction.",
                  )}
                </p>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2 text-xs font-semibold text-[#4E5C87] sm:grid-cols-3">
                <span className="inline-flex items-center gap-2">
                  <HiPhone className="text-base text-[#2C3EA2]" />
                  {displayValue(teacher?.MobileNumber)}
                </span>
                <span className="inline-flex items-center gap-2">
                  <HiMail className="text-base text-[#2C3EA2]" />
                  {displayValue(teacher?.EmailId)}
                </span>
                <span className="inline-flex items-center gap-2">
                  <HiLocationMarker className="text-base text-[#2C3EA2]" />
                  {displayValue(teacher?.District)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 border-t border-[#E5E9F5] bg-white px-4 py-4 shadow-[0_-10px_28px_rgba(16,25,66,0.1)] md:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="text-left">
            <h2 className="text-sm md:text-base text-[#101942] font-bold">
              {getLabel(
                "confirm_once_again",
                "Please confirm the details once again",
              )}
            </h2>
            <p className="mt-1 text-xs md:text-sm text-[#667085]">
              {getLabel(
                "confirm_teacher_details_note",
                "Your confirmation will be used to continue the teacher ID flow.",
              )}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row md:justify-end">
            <button
              className="w-full sm:w-[140px] bg-[#E7EAFF] text-[#3C57AD] px-8 py-3 rounded-[10px] font-semibold transition hover:bg-[#DDE3FF] disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleCancelTeacherDetail}
              disabled={confirmTeacherLoading}
            >
              {getLabel("not_txt", "No")}
            </button>

            <button
              className={`w-full sm:w-[150px] text-white px-8 py-3 rounded-[10px] flex items-center justify-center gap-2 font-semibold transition ${
                confirmTeacherLoading
                  ? "bg-[#3a3e58] cursor-wait"
                  : "bg-[#25359C] hover:bg-[#1D2B80] hover:shadow-lg"
              }`}
              onClick={handleConfirmTeacherDetail}
              disabled={confirmTeacherLoading}
            >
              {confirmTeacherLoading ? (
                <>
                  <span className="spinner-border animate-spin inline-block w-4 h-4 border-4 rounded-full"></span>
                  <span>{getLabel("label_loading", "Loading")}...</span>
                </>
              ) : (
                getLabel("yes_txt", "Yes")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherIdProfileCard;
